import { type FormEvent, useEffect, useId, useRef, useState } from "react";

import { errorCodeOf, errorMessageOf } from "./patientAuthErrors";
import { type OneTimePasswordSender, signIn } from "./patientAuthenticationApi";
import {
  MIGRATION_NOTICE_BODY,
  MIGRATION_NOTICE_TITLE,
  MIGRATION_TEMPORARY_PASSWORD_HINT,
  NOTICE_MESSAGES,
} from "./patientAuthMessages";
import {
  Actions,
  BlockButton,
  Divider,
  ErrorMessage,
  Field,
  Heading,
  InfoMessage,
  Muted,
  SuccessMessage,
} from "./patientAuthenticationParts";
import type { PatientLoginNotice } from "./patientLoginPath";

type Props = {
  initialLoginId: string;
  /** 前の段から戻ったときのエラー（ログインの途中の PATIENT_AUTH_SESSION_EXPIRED） */
  initialError: string;
  notice: PatientLoginNotice | null;
  /** 前の段から戻ったとき、見出しへフォーカスを移す（エラーがあればパスワードの欄へ移す） */
  focusHeadingOnMount?: boolean;
  onOneTimePasswordRequired: (
    loginId: string,
    oneTimeToken: string,
    sender: OneTimePasswordSender,
  ) => void;
  onSignedIn: () => void;
};

export function PatientSignInForm({
  initialLoginId,
  initialError,
  notice,
  focusHeadingOnMount = false,
  onOneTimePasswordRequired,
  onSignedIn,
}: Props) {
  const id = useId();
  const [loginId, setLoginId] = useState(initialLoginId);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(initialError);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorTick, setErrorTick] = useState(0);
  const passwordRef = useRef<HTMLInputElement>(null);

  // エラーを出したら、パスワードの欄へフォーカスを移す
  useEffect(() => {
    if (error) passwordRef.current?.focus();
  }, [error, errorTick]);

  const filled = loginId.trim() !== "" && password !== "";
  const errorId = `${id}-error`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!filled || loading) return;
    setLoading(true);
    setError("");
    setFailed(false);
    let result;
    try {
      result = await signIn(loginId.trim(), password);
    } catch (e) {
      setPassword("");
      setFailed(errorCodeOf(e) === "PATIENT_AUTH_FAILED");
      setError(errorMessageOf(e));
      setErrorTick((tick) => tick + 1);
      setLoading(false);
      return;
    }
    // 次の段へ移るか画面を移すまで、押せないままにする
    if (result.oneTimePasswordRequired) {
      onOneTimePasswordRequired(
        loginId.trim(),
        result.oneTimeToken,
        result.oneTimePasswordSender,
      );
    } else {
      onSignedIn();
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      {/* 案内とエラーが同時に出るときも、案内は残す（計画の M7 の「入力欄とメッセージの決まり」） */}
      {notice === "expired" && (
        <InfoMessage>{NOTICE_MESSAGES.expired}</InfoMessage>
      )}
      {notice === "signed-out" && (
        <SuccessMessage>{NOTICE_MESSAGES["signed-out"]}</SuccessMessage>
      )}
      <InfoMessage title={MIGRATION_NOTICE_TITLE}>
        {MIGRATION_NOTICE_BODY}
      </InfoMessage>
      <Heading focusOnMount={focusHeadingOnMount && !initialError}>
        ログイン
      </Heading>
      {error && (
        <ErrorMessage id={errorId}>
          {error}
          {failed && (
            <span className="block mt-1 text-gray-800">
              {MIGRATION_TEMPORARY_PASSWORD_HINT}
            </span>
          )}
        </ErrorMessage>
      )}
      <Field
        id={`${id}-login-id`}
        label="ログインID（メールアドレス）"
        type="email"
        inputMode="email"
        autoComplete="username"
        placeholder="example@example.com"
        value={loginId}
        onChange={(e) => setLoginId(e.target.value)}
        disabled={loading}
      />
      <Field
        id={`${id}-password`}
        ref={passwordRef}
        label="パスワード"
        password
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        help={
          filled
            ? undefined
            : "ログインIDとパスワードを入力すると「ログイン」を押せます。"
        }
        extraDescribedBy={error ? errorId : undefined}
      />
      <Actions>
        <BlockButton type="submit" disabled={!filled || loading}>
          {loading ? "送信中…" : "ログイン"}
        </BlockButton>
      </Actions>
      <Divider />
      <Muted>パスワードを忘れた方は、お問い合わせ窓口へご連絡ください。</Muted>
    </form>
  );
}
